import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Alert, Button } from '@truecare/ui';
import { api } from '../api';
import { PortalShell } from '../components/PortalShell';

// Token comes from ?token=… in the confirm-email URL sent to the NEW address.
const SearchSchema = z.object({
  token: z.string().min(1),
});

export const Route = createFileRoute('/email-change/verify')({
  validateSearch: (search) => SearchSchema.parse(search),
  component: EmailChangeVerifyPage,
});

/**
 * Email-change confirm page. Opened from the link in the confirmation email
 * sent to the user's NEW address. JS extracts the token and POSTs to
 * /api/v1/auth/email-change/confirm — POST-not-GET so email scanners that
 * pre-fetch the URL don't burn the single-use token. On success the backend
 * applies the new email, revokes all prior sessions, and sets a fresh session
 * cookie on THIS device, so we land the user on /me signed in with the new
 * address.
 */
function EmailChangeVerifyPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  // Run-once + mounted guards — identical rationale to the magic-link verify
  // page: the token is single-use, so the confirm POST must fire exactly once
  // per load despite React strict-mode's double-invoke.
  const verifyStarted = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    if (verifyStarted.current) {
      return () => {
        mounted.current = false;
      };
    }
    verifyStarted.current = true;
    void (async () => {
      try {
        const { error: respErr, response } = await api.POST(
          '/api/v1/auth/email-change/confirm',
          { body: { token } },
        );
        if (!mounted.current) return;
        if (respErr) {
          setError(
            response?.status === 409
              ? 'That email address is now in use, so your email was not changed. Your account still uses your previous email.'
              : 'This confirmation link is invalid or has expired. You can request a new email change from your profile.',
          );
          return;
        }
        // Success — a fresh session cookie is set on this device (all prior
        // sessions were revoked). That fresh session has totpSatisfiedAt=null.
        // 2026-10-18 audit (MED): operators (always TOTP-enrolled) must complete
        // the second factor here, exactly like sign-in / magic-link-verify /
        // password-reset do — otherwise they land in the admin app with every
        // /admin/* query 403-ing (totp_verification_required) and no in-app
        // recovery. Route TOTP users through /sign-in/totp; non-TOTP personas
        // (caregiver / homecare) fall through to /me.
        await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
        if (!mounted.current) return;
        const meResp = await api.GET('/api/v1/auth/me', {});
        if (!mounted.current) return;
        if (meResp.data?.totpEnrolled === true) {
          await navigate({ to: '/sign-in/totp', search: {} });
          return;
        }
        await navigate({ to: '/me' });
      } catch {
        if (!mounted.current) return;
        setError(
          'Something went wrong confirming your email. Please try again from your profile.',
        );
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, [token, navigate, qc]);

  return (
    <PortalShell signedOutSurface>
      <div className="mx-auto max-w-md px-6 pb-16 pt-20">
        {error ? (
          <>
            <Alert tone="danger" title="Email not changed">
              {error}
            </Alert>
            <Link to="/me">
              <Button variant="primary" className="mt-6 w-full">
                Go to your profile
              </Button>
            </Link>
          </>
        ) : (
          <div className="text-center">
            <h1 className="font-heading text-2xl text-forest">
              Confirming your new email…
            </h1>
            <p className="mt-3 font-body text-sm text-ink-muted">
              One moment while we update your account.
            </p>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
